<!-- Header -->
<div class="d-flex flex-wrap flex-stack mb-6">
  <div>
    <h3 class="fw-bold my-2">{{ 'membership.title' | translate }}</h3>
    <p class="text-muted fs-6 mb-0">{{ 'membership.subtitle' | translate }}</p>
  </div>
  <div class="d-flex flex-wrap my-2">
    <app-button
      [variant]="'primary'"
      [label]="'membership.purchaseMembership' | translate"
      [iconClass]="'fa fa-plus'"
      (clicked)="openPurchaseMembershipModal()"
    />
  </div>
</div>

<!-- Empty State -->
@if (!memberShips || memberShips.length === 0) {
  <div class="card mb-9">
    <div class="empty-state">
      <div class="empty-state-icon">
        <i class="fa-solid fa-id-card"></i>
      </div>
      <h4>{{ 'membership.noActive' | translate }}</h4>
      <p>{{ 'membership.noActiveHint' | translate }}</p>
      <app-button
        [variant]="'primary'"
        [label]="'membership.browsePlans' | translate"
        [iconClass]="'fa fa-arrow-right'"
        (clicked)="openPurchaseMembershipModal()"
      />
    </div>
  </div>
}

<!-- Active Memberships -->
@if (getActiveMemberships().length > 0) {
  <div class="mb-8">
    <div class="section-header mb-5">
      <i class="fa fa-check-circle text-success me-2"></i>
      <span class="fw-bold fs-4">{{ 'membership.activeMemberships' | translate }}</span>
      <span class="badge badge-light-success ms-2">{{ getActiveMemberships().length }}</span>
    </div>

    <div class="row g-5">
      @for (membership of getActiveMemberships(); track membership.membership_id; let i = $index) {
        <div class="col-md-6 col-lg-4">
          <div class="card h-100 membership-card">
            <!-- Header with gradient or company logo -->
            <div class="membership-image-container position-relative" [class.family]="membership.plan?.coverage === 'family'" [class.has-logo]="membership.plan?.company?.company_logo_url">
              @if (membership.plan?.company?.company_logo_url) {
                <img [src]="membership.plan?.company?.company_logo_url" [alt]="membership.plan?.company?.company_name" class="company-logo"/>
              } @else {
                <div class="membership-icon">
                  @if (membership.plan?.coverage === 'family') {
                    <i class="fa-solid fa-users"></i>
                  } @else {
                    <i class="fa-solid fa-user"></i>
                  }
                </div>
              }
              <div class="position-absolute top-0 end-0 m-2">
                <span class="badge badge-success">{{ 'membership.active' | translate }}</span>
              </div>
              <div class="position-absolute top-0 start-0 m-2">
                <span class="badge" [class.badge-primary]="membership.plan?.coverage === 'individual'" [class.badge-info]="membership.plan?.coverage === 'family'">
                  {{ membership.plan?.coverage === 'family' ? ('membership.family' | translate) : ('membership.individual' | translate) }}
                </span>
              </div>
            </div>

            <div class="card-body d-flex flex-column p-4">
              <!-- Plan Name -->
              <h5 class="card-title fw-bold mb-1">{{ membership.plan?.name }}</h5>
              <p class="text-muted fs-7 mb-3">{{ membership.plan?.company?.company_name }}</p>

              <!-- Details -->
              <div class="membership-details mb-3">
                <!-- Expires -->
                <div class="detail-row">
                  <i class="fa fa-calendar-check text-primary"></i>
                  <div>
                    <span class="fw-semibold">{{ 'membership.expires' | translate }} {{ formatTimeYMD(membership.expires_at) }}</span>
                    @if (isExpiringSoon(membership)) {
                      <span class="text-warning ms-1">({{ getDaysRemaining(membership) }} {{ 'membership.daysLeft' | translate }})</span>
                    }
                  </div>
                </div>

                <!-- Billing -->
                <div class="detail-row">
                  <i class="fa fa-sync text-primary"></i>
                  <span>{{ membership.plan?.billing_cycle | titlecase }} {{ 'membership.billing' | translate }}</span>
                </div>

                <!-- Family Members -->
                @if (membership.plan?.coverage === 'family' && membership.family) {
                  <div class="detail-row">
                    <i class="fa fa-users text-primary"></i>
                    <span>{{ membership.family.members.length }} {{ 'membership.members' | translate }}
                      @if (membership.plan?.max_children) {
                        <span class="text-muted">({{ 'membership.max' | translate }} {{ membership.plan?.max_children }})</span>
                      }
                    </span>
                  </div>
                }
              </div>

              <!-- Benefits -->
              @if (membership.plan?.benefits && membership.plan!.benefits.length > 0) {
                <div class="membership-benefits mb-3">
                  <div class="benefits-label text-muted fs-7 mb-2">{{ 'membership.benefits' | translate }}</div>
                  <ul class="benefits-list">
                    @for (benefit of membership.plan!.benefits.slice(0, 3); track benefit) {
                      <li>
                        <i class="fa fa-check text-success me-2"></i>
                        <span>{{ benefit }}</span>
                      </li>
                    }
                    @if (membership.plan!.benefits.length > 3) {
                      <li class="more-benefits text-muted">
                        <i class="fa fa-plus me-2"></i>
                        <span>{{ membership.plan!.benefits.length - 3 }} {{ 'membership.moreBenefits' | translate }}</span>
                      </li>
                    }
                  </ul>
                </div>
              }

              <!-- Family Section -->
              @if (membership.plan?.coverage === 'family' && membership.family) {
                <div class="family-preview mb-3">
                  <div class="family-avatars">
                    @for (member of membership.family.members.slice(0, 4); track member.user.user_id) {
                      <div class="family-avatar" [title]="member.user.full_name">
                        @if (member.user.profile_image_url) {
                          <img [src]="member.user.profile_image_url" [alt]="member.user.full_name"/>
                        } @else {
                          <span>{{ getInitials(member.user.full_name) }}</span>
                        }
                      </div>
                    }
                    @if (membership.family.members.length > 4) {
                      <div class="family-avatar more">+{{ membership.family.members.length - 4 }}</div>
                    }
                  </div>
                </div>
              }

              <!-- Actions -->
              <div class="mt-auto d-flex gap-2">
                @if (membership.plan?.coverage === 'family' && membership.family) {
                  <app-button
                    [variant]="'primary'"
                    [label]="'membership.manage' | translate"
                    [iconClass]="'fa fa-users'"
                    class="flex-grow-1"
                    (clicked)="toggleFamily(i); openMembershipModal(membership.family!.family_id, membership.family!.members)"
                  />
                }
                <app-button
                  [variant]="'outline'"
                  [label]="'membership.cancel' | translate"
                  [class]="membership.plan?.coverage === 'family' ? '' : 'flex-grow-1'"
                  (clicked)="cancelMembership(membership)"
                />
              </div>
            </div>
          </div>
        </div>
      }
    </div>
  </div>
}

<!-- Expired Memberships -->
@if (getExpiredMemberships().length > 0) {
  <div class="mb-8">
    <div class="section-header mb-5">
      <i class="fa fa-clock text-warning me-2"></i>
      <span class="fw-bold fs-4">{{ 'membership.expiredMemberships' | translate }}</span>
      <span class="badge badge-light-warning ms-2">{{ getExpiredMemberships().length }}</span>
    </div>

    <div class="row g-5">
      @for (membership of getExpiredMemberships(); track membership.membership_id) {
        <div class="col-md-6 col-lg-4">
          <div class="card h-100 membership-card expired">
            <!-- Header with gradient or company logo -->
            <div class="membership-image-container expired position-relative" [class.has-logo]="membership.plan?.company?.company_logo_url">
              @if (membership.plan?.company?.company_logo_url) {
                <img [src]="membership.plan?.company?.company_logo_url" [alt]="membership.plan?.company?.company_name" class="company-logo"/>
              } @else {
                <div class="membership-icon">
                  @if (membership.plan?.coverage === 'family') {
                    <i class="fa-solid fa-users"></i>
                  } @else {
                    <i class="fa-solid fa-user"></i>
                  }
                </div>
              }
              <div class="position-absolute top-0 end-0 m-2">
                <span class="badge badge-warning">{{ 'membership.expired' | translate }}</span>
              </div>
              <div class="position-absolute top-0 start-0 m-2">
                <span class="badge" [class.badge-primary]="membership.plan?.coverage === 'individual'" [class.badge-info]="membership.plan?.coverage === 'family'">
                  {{ membership.plan?.coverage === 'family' ? ('membership.family' | translate) : ('membership.individual' | translate) }}
                </span>
              </div>
            </div>

            <div class="card-body d-flex flex-column p-4">
              <!-- Plan Name -->
              <h5 class="card-title fw-bold mb-1">{{ membership.plan?.name }}</h5>
              <p class="text-muted fs-7 mb-3">{{ membership.plan?.company?.company_name }}</p>

              <!-- Details -->
              <div class="membership-details mb-4">
                <!-- Expired Date -->
                <div class="detail-row">
                  <i class="fa fa-calendar-xmark text-warning"></i>
                  <div>
                    <span class="text-warning fw-semibold">{{ 'membership.expiredOn' | translate }} {{ formatTimeYMD(membership.expires_at) }}</span>
                  </div>
                </div>

                <!-- Billing -->
                <div class="detail-row">
                  <i class="fa fa-sync text-muted"></i>
                  <span>{{ membership.plan?.billing_cycle | titlecase }} {{ 'membership.billing' | translate }}</span>
                </div>

                <!-- Coverage -->
                <div class="detail-row">
                  <i class="fa fa-id-card text-muted"></i>
                  <span>{{ membership.plan?.coverage === 'family' ? ('membership.family' | translate) : ('membership.individual' | translate) }}</span>
                </div>
              </div>

              <!-- Action -->
              <div class="mt-auto">
                <app-button
                  [variant]="'primary'"
                  [label]="'membership.renewMembership' | translate"
                  [iconClass]="'fa fa-refresh'"
                  class="w-100"
                  (clicked)="renewMembership(membership)"
                />
              </div>
            </div>
          </div>
        </div>
      }
    </div>
  </div>
}

<!-- Divider -->
@if ((getActiveMemberships().length > 0 || getExpiredMemberships().length > 0) && getInactiveMemberships().length > 0) {
  <div class="separator-section my-8">
    <div class="d-flex align-items-center">
      <div class="flex-grow-1 border-bottom border-gray-300"></div>
      <div class="px-4">
        <span class="badge badge-light-secondary">{{ 'membership.cancelledMemberships' | translate }}</span>
      </div>
      <div class="flex-grow-1 border-bottom border-gray-300"></div>
    </div>
  </div>
}

<!-- Inactive Memberships -->
@if (getInactiveMemberships().length > 0) {
  <div class="mb-9">
    @if (getActiveMemberships().length === 0) {
      <div class="section-header mb-5">
        <i class="fa fa-history text-secondary me-2"></i>
        <span class="fw-bold fs-4">{{ 'membership.inactiveMemberships' | translate }}</span>
        <span class="badge badge-light-secondary ms-2">{{ getInactiveMemberships().length }}</span>
      </div>
    }

    <div class="row g-5">
      @for (membership of getInactiveMemberships(); track membership.membership_id) {
        <div class="col-md-6 col-lg-4">
          <div class="card h-100 membership-card inactive">
            <!-- Header with gradient or company logo -->
            <div class="membership-image-container inactive position-relative" [class.has-logo]="membership.plan?.company?.company_logo_url">
              @if (membership.plan?.company?.company_logo_url) {
                <img [src]="membership.plan?.company?.company_logo_url" [alt]="membership.plan?.company?.company_name" class="company-logo"/>
              } @else {
                <div class="membership-icon">
                  @if (membership.plan?.coverage === 'family') {
                    <i class="fa-solid fa-users"></i>
                  } @else {
                    <i class="fa-solid fa-user"></i>
                  }
                </div>
              }
              <div class="position-absolute top-0 end-0 m-2">
                <span class="badge badge-secondary">{{ membership.status | titlecase }}</span>
              </div>
            </div>

            <div class="card-body d-flex flex-column p-4">
              <!-- Plan Name -->
              <h5 class="card-title fw-bold mb-1">{{ membership.plan?.name }}</h5>
              <p class="text-muted fs-7 mb-3">{{ membership.plan?.company?.company_name }}</p>

              <!-- Details -->
              <div class="membership-details mb-4">
                <!-- Cancelled date -->
                <div class="detail-row">
                  <i class="fa fa-ban text-muted"></i>
                  <span>{{ membership.status | titlecase }}</span>
                </div>

                <!-- Coverage -->
                <div class="detail-row">
                  <i class="fa fa-id-card text-muted"></i>
                  <span>{{ membership.plan?.coverage === 'family' ? ('membership.family' | translate) : ('membership.individual' | translate) }}</span>
                </div>
              </div>

              <!-- Action -->
              <div class="mt-auto">
                <app-button
                  [variant]="'primary'"
                  [label]="'membership.rejoin' | translate"
                  [iconClass]="'fa fa-refresh'"
                  class="w-100"
                  (clicked)="renewMembership(membership)"
                />
              </div>
            </div>
          </div>
        </div>
      }
    </div>
  </div>
}
